import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import {
  Camera, Maximize, Minimize, SlidersHorizontal, X, Search, MapPin,
  VideoOff, LayoutGrid, Clock, Wifi, WifiOff, Monitor, RefreshCw, Check,
  Bookmark, BookmarkPlus, ChevronDown, ChevronUp, Trash2, AlertOctagon, Volume2, VolumeX, Film, RotateCw,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

/**
 * CCTV NOC Wall — Business / Admin only.
 * Auto-refreshing grid of NZTA traffic-camera stills (refresh every 30s).
 * The operator picks which cameras appear via a searchable checkbox dropdown;
 * the selection + layout persist in localStorage so a wall TV survives reloads.
 */

const LAYOUTS = {
  4: { cols: 2, rows: 2, label: '2 × 2' },
  6: { cols: 3, rows: 2, label: '3 × 2' },
  8: { cols: 4, rows: 2, label: '4 × 2' },
  9: { cols: 3, rows: 3, label: '3 × 3' },
  12: { cols: 4, rows: 3, label: '4 × 3' },
};

// Near-live: NZTA refreshes stills ~every 30–60s; we re-pull every 12s so new frames
// appear as soon as they're published. Motion mode loops the last few frames per tile.
const REFRESH_MS = 12000;
const MOTION_FRAMES = 8;      // frames buffered per tile for time-lapse playback
const MOTION_PLAY_MS = 450;   // playback speed in motion mode
const ROTATE_MS = 15000;      // auto-rotate: seconds per page of cameras

const LS_SELECTED = 'ptt_cctv_selected';
const LS_LAYOUT = 'ptt_cctv_layout';
const LS_PRESETS = 'ptt_cctv_presets';
const LS_SOUND = 'ptt_cctv_sound';
const LS_MOTION = 'ptt_cctv_motion';
const LS_ROTATE = 'ptt_cctv_rotate';

function loadSelected() {
  try {
    const v = JSON.parse(localStorage.getItem(LS_SELECTED) || '[]');
    return Array.isArray(v) ? v.map(String) : [];
  } catch { return []; }
}
function loadLayout() {
  const n = parseInt(localStorage.getItem(LS_LAYOUT) || '4', 10);
  return LAYOUTS[n] ? n : 4;
}
function loadPresets() {
  try {
    const v = JSON.parse(localStorage.getItem(LS_PRESETS) || '[]');
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}

// ---- alert-layer helpers ----
const hwNorm = (h) => (h || '').toUpperCase().replace(/\s+/g, '');
const sevRank = (s) => (s === 'red' ? 3 : s === 'orange' ? 2 : s === 'yellow' ? 1 : 0);
function kmDist(aLat, aLng, bLat, bLng) {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat), dLng = toRad(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
function alertLabel(a) {
  const t = (a.event_type || a.cause || '').trim();
  if (a.severity === 'red') return t && /clos/i.test(t) ? 'ROAD CLOSED' : (t ? t.toUpperCase().slice(0, 24) : 'CLOSURE');
  return t ? t.toUpperCase().slice(0, 24) : 'INCIDENT';
}

export default function CameraNoc() {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(loadSelected);   // ordered array of camera ids
  const [layout, setLayout] = useState(loadLayout);
  const [configOpen, setConfigOpen] = useState(false);
  const [tick, setTick] = useState(Date.now());             // image cache-bust token
  const [now, setNow] = useState(dayjs());
  const [fs, setFs] = useState(false);
  const [feed, setFeed] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [presets, setPresets] = useState(loadPresets);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem(LS_SOUND) !== 'false');
  const [motion, setMotion] = useState(() => localStorage.getItem(LS_MOTION) === 'true');
  const [rotate, setRotate] = useState(() => localStorage.getItem(LS_ROTATE) === 'true');
  const [page, setPage] = useState(0);
  const audioCtxRef = useRef(null);
  const prevRedRef = useRef(null);

  // ---- data ----
  const loadCameras = useCallback(async () => {
    try {
      const { data } = await api.get('/cameras');
      setCameras(data || []);
    } catch { /* route already guards access */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCameras(); }, [loadCameras]);

  // feed status (for the header pill)
  useEffect(() => {
    const f = async () => {
      try {
        const { data } = await api.get('/status/feeds');
        setFeed((data.feeds || []).find((x) => x.key === 'cameras') || null);
      } catch { /* noop */ }
    };
    f();
    const iv = setInterval(f, 60000);
    return () => clearInterval(iv);
  }, []);

  // active alerts for the "alert layer" (red/orange on a camera's highway right now)
  useEffect(() => {
    const f = async () => {
      try { const { data } = await api.get('/alerts'); setAlerts(data || []); } catch { /* noop */ }
    };
    f();
    const iv = setInterval(f, 60000);
    return () => clearInterval(iv);
  }, []);

  // image refresh + clock + wake lock
  useEffect(() => {
    const imgIv = setInterval(() => setTick(Date.now()), REFRESH_MS);
    const clockIv = setInterval(() => setNow(dayjs()), 1000);
    const beatIv = setInterval(() => api.get('/auth/me').catch(() => {}), 20 * 60 * 1000);
    let wakeLock = null;
    (async () => {
      try { if ('wakeLock' in navigator) wakeLock = await navigator.wakeLock.request('screen'); } catch { /* noop */ }
    })();
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => {
      clearInterval(imgIv); clearInterval(clockIv); clearInterval(beatIv);
      document.removeEventListener('fullscreenchange', onFs);
      if (wakeLock) wakeLock.release?.();
    };
  }, []);

  // persist
  useEffect(() => { localStorage.setItem(LS_SELECTED, JSON.stringify(selected)); }, [selected]);
  useEffect(() => { localStorage.setItem(LS_LAYOUT, String(layout)); }, [layout]);
  useEffect(() => { localStorage.setItem(LS_PRESETS, JSON.stringify(presets)); }, [presets]);
  useEffect(() => { localStorage.setItem(LS_SOUND, String(soundOn)); }, [soundOn]);
  useEffect(() => { localStorage.setItem(LS_MOTION, String(motion)); }, [motion]);
  useEffect(() => { localStorage.setItem(LS_ROTATE, String(rotate)); }, [rotate]);

  const byId = useMemo(() => {
    const m = {}; cameras.forEach((c) => { m[c.id] = c; }); return m;
  }, [cameras]);

  const capacity = layout;
  const totalPages = Math.max(1, Math.ceil(selected.length / capacity));
  const activePage = rotate ? (page % totalPages) : 0;
  const shown = useMemo(() => {
    const start = activePage * capacity;
    return selected.slice(start, start + capacity).map((id) => byId[id]).filter(Boolean);
  }, [selected, byId, capacity, activePage]);

  // auto-rotate: advance a page of cameras on a timer (only when there's more than one page)
  useEffect(() => {
    if (!rotate) { setPage(0); return undefined; }
    if (totalPages <= 1) return undefined;
    const iv = setInterval(() => setPage((p) => (p + 1) % totalPages), ROTATE_MS);
    return () => clearInterval(iv);
  }, [rotate, totalPages]);

  // camera-id -> worst active red/orange alert on the same highway & nearby
  const camAlert = useMemo(() => {
    const map = {};
    const active = alerts.filter(
      (a) => (a.status || '').toLowerCase() !== 'resolved' && (a.severity === 'red' || a.severity === 'orange')
    );
    for (const cam of shown) {
      const chw = hwNorm(cam.highway);
      if (!chw) continue;
      let best = null;
      for (const a of active) {
        if (hwNorm(a.highway) !== chw) continue;
        if (a.lat && a.lng && cam.lat && cam.lng) {
          if (kmDist(a.lat, a.lng, cam.lat, cam.lng) > 45) continue;   // same highway but far away
        } else if (a.island && cam.island && a.island !== cam.island) {
          continue;
        }
        if (!best || sevRank(a.severity) > sevRank(best.severity)) best = a;
      }
      if (best) map[cam.id] = best;
    }
    return map;
  }, [alerts, shown]);

  const loadPreset = (p) => { setSelected(p.cameras || []); if (LAYOUTS[p.layout]) setLayout(p.layout); };

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const removeCam = (id) => setSelected((prev) => prev.filter((x) => x !== id));

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch { /* noop */ }
  };

  // ---- alert sound cue ----
  const ensureAudio = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) audioCtxRef.current = new Ctx();
      }
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();
    } catch { /* noop */ }
    return audioCtxRef.current;
  }, []);

  const playChime = useCallback(() => {
    const ctx = ensureAudio();
    if (!ctx) return;
    const t0 = ctx.currentTime;
    // soft two-note "ding-dong"
    [[880, 0], [1174.66, 0.16]].forEach(([freq, dt]) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      o.connect(g); g.connect(ctx.destination);
      const s = t0 + dt;
      g.gain.setValueAtTime(0.0001, s);
      g.gain.exponentialRampToValueAtTime(0.22, s + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, s + 0.6);
      o.start(s); o.stop(s + 0.65);
    });
  }, [ensureAudio]);

  // browsers need a user gesture before audio can play — arm it on first interaction
  useEffect(() => {
    const arm = () => ensureAudio();
    window.addEventListener('pointerdown', arm);
    window.addEventListener('keydown', arm);
    return () => { window.removeEventListener('pointerdown', arm); window.removeEventListener('keydown', arm); };
  }, [ensureAudio]);

  // chime when a NEW camera tile flips to a red closure (skip the first baseline pass)
  useEffect(() => {
    const redIds = new Set(
      Object.entries(camAlert).filter(([, a]) => a.severity === 'red').map(([id]) => id)
    );
    if (prevRedRef.current === null) { prevRedRef.current = redIds; return; }
    let fresh = false;
    redIds.forEach((id) => { if (!prevRedRef.current.has(id)) fresh = true; });
    prevRedRef.current = redIds;
    if (fresh && soundOn) playChime();
  }, [camAlert, soundOn, playChime]);

  const { cols, rows } = LAYOUTS[layout];
  const slots = Array.from({ length: capacity });

  return (
    <div className="noc grain min-h-screen flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <img src="/brand/logo-icon@2x.png" alt="PTT" className="w-9 h-9 rounded-lg" />
          <div>
            <div className="font-display font-black text-xl leading-none tracking-tight">
              PTT NZ ALERTS · CCTV WALL
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-teal-400 mt-1">
              Waka Kotahi NZTA Traffic Cameras
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <FeedPill feed={feed} />
          <div className="text-right hidden md:block">
            <div className="font-display font-black text-2xl leading-none tabular-nums">{now.format('HH:mm:ss')}</div>
            <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-400 mt-0.5">{now.format('ddd D MMM')}</div>
          </div>

          {/* Layout selector */}
          <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1" data-testid="cctv-layout">
            <LayoutGrid className="w-4 h-4 text-zinc-400 ml-1" />
            {Object.keys(LAYOUTS).map((n) => (
              <button
                key={n}
                onClick={() => setLayout(Number(n))}
                data-testid={`cctv-layout-${n}`}
                title={`${n} cameras (${LAYOUTS[n].label})`}
                className={`px-2.5 py-1 rounded-md text-sm font-semibold transition ${
                  layout === Number(n) ? 'bg-teal-500 text-black' : 'text-white hover:bg-zinc-600'
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          <PresetsMenu presets={presets} setPresets={setPresets} current={selected} layout={layout} onLoad={loadPreset} />

          <button
            onClick={() => setConfigOpen((o) => !o)}
            data-testid="cctv-configure"
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition ${
              configOpen ? 'bg-teal-500 text-black' : 'bg-white/5 text-zinc-200 hover:bg-white/10'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" /> Cameras
            <span className="text-xs opacity-70">({selected.length})</span>
          </button>

          <Link to="/noc" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-200" title="NOC overview" data-testid="cctv-to-noc">
            <Monitor className="w-4 h-4" />
          </Link>
          <button
            onClick={() => setRotate((r) => !r)}
            data-testid="cctv-rotate"
            title={rotate ? `Auto-rotate on — ${totalPages} page(s), ${ROTATE_MS / 1000}s each` : 'Auto-rotate off (sweep all selected cameras)'}
            className={`inline-flex items-center gap-1.5 p-2 rounded-lg transition ${rotate ? 'bg-teal-500 text-black' : 'bg-white/5 text-zinc-200 hover:bg-white/10'}`}
          >
            <RotateCw className={`w-4 h-4 ${rotate && totalPages > 1 ? 'animate-spin-slow' : ''}`} />
            {rotate && totalPages > 1 && <span className="text-xs font-semibold" data-testid="cctv-rotate-page">{activePage + 1}/{totalPages}</span>}
          </button>
          <button
            onClick={() => setMotion((m) => !m)}
            data-testid="cctv-motion"
            title={motion ? 'Motion mode on (time-lapse)' : 'Motion mode off (latest still)'}
            className={`p-2 rounded-lg transition ${motion ? 'bg-teal-500 text-black' : 'bg-white/5 text-zinc-200 hover:bg-white/10'}`}
          >
            <Film className="w-4 h-4" />
          </button>
          <button
            onClick={() => { setSoundOn((s) => !s); ensureAudio(); }}
            data-testid="cctv-sound"
            title={soundOn ? 'Mute red-closure chime' : 'Unmute red-closure chime'}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-200"
          >
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button onClick={toggleFullscreen} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-200" title="Fullscreen" data-testid="cctv-fullscreen">
            {fs ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="p-3" style={{ height: 'calc(100vh - 66px)' }}>
        {shown.length === 0 ? (
          <EmptyState onOpen={() => setConfigOpen(true)} loading={loading} />
        ) : (
          <div
            className="grid gap-3 h-full"
            style={{
              gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
            }}
            data-testid="cctv-grid"
          >
            {slots.map((_, i) => {
              const cam = shown[i];
              return cam
                ? <CameraTile key={cam.id} cam={cam} tick={tick} motion={motion} alert={camAlert[cam.id]} onRemove={() => removeCam(cam.id)} />
                : <EmptySlot key={`empty-${i}`} onClick={() => setConfigOpen(true)} />;
            })}
          </div>
        )}
      </div>

      {/* Configure dropdown */}
      {configOpen && (
        <ConfigPanel
          cameras={cameras}
          selected={selected}
          capacity={capacity}
          onToggle={toggle}
          onClear={() => setSelected([])}
          onClose={() => setConfigOpen(false)}
        />
      )}
    </div>
  );
}

function CameraTile({ cam, tick, motion, alert, onRemove }) {
  const [errored, setErrored] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const [frames, setFrames] = useState([]);   // recent loaded frame URLs (for motion playback)
  const [playIdx, setPlayIdx] = useState(0);
  const latestSrc = cam.image_url ? `${cam.image_url}?_t=${tick}` : null;

  // preload each refresh frame in the background, then append to the buffer
  useEffect(() => {
    if (!latestSrc) { setErrored(true); return; }
    const img = new window.Image();
    img.referrerPolicy = 'no-referrer';
    img.onload = () => {
      setErrored(false);
      setLoadedAt(Date.now());
      setFrames((prev) => [...prev, latestSrc].slice(-MOTION_FRAMES));
    };
    img.onerror = () => setErrored(true);
    img.src = latestSrc;
  }, [latestSrc]);

  // motion playback: cycle through buffered frames
  useEffect(() => {
    if (!motion) return undefined;
    const iv = setInterval(() => setPlayIdx((i) => i + 1), MOTION_PLAY_MS);
    return () => clearInterval(iv);
  }, [motion]);

  const displaySrc = motion && frames.length > 0
    ? frames[playIdx % frames.length]
    : (frames.length ? frames[frames.length - 1] : latestSrc);
  const offline = cam.offline || (errored && frames.length === 0) || !latestSrc;
  const alertColor = alert ? (alert.severity === 'red' ? '#E11D48' : '#F7941D') : null;

  return (
    <div
      className="noc-card relative overflow-hidden group"
      data-testid={`cctv-tile-${cam.id}`}
      data-alert={alert ? alert.severity : ''}
      style={alertColor ? { boxShadow: `0 0 0 3px ${alertColor}, 0 0 22px ${alertColor}55`, borderColor: alertColor } : undefined}
    >
      {offline ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-600 gap-2">
          <VideoOff className="w-8 h-8" />
          <span className="text-xs uppercase tracking-widest">{cam.offline ? 'Offline / maintenance' : 'No image'}</span>
        </div>
      ) : (
        <img
          src={displaySrc}
          alt={cam.name}
          referrerPolicy="no-referrer"
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* motion badge */}
      {motion && !offline && (
        <div className="absolute top-2 left-2 z-20 flex items-center gap-1 px-2 py-0.5 rounded bg-black/60 text-teal-300 text-[10px] font-bold uppercase tracking-wide" data-testid={`cctv-motion-badge-${cam.id}`}>
          <Film className="w-3 h-3" /> Motion
        </div>
      )}

      {/* live-alert banner for this camera's highway */}
      {alert && (
        <div
          className="absolute top-0 inset-x-0 z-20 px-3 py-1.5 flex items-center gap-1.5 text-white text-xs font-black tracking-wide pointer-events-none"
          style={{ background: alertColor }}
          data-testid={`cctv-alert-${cam.id}`}
        >
          <AlertOctagon className={`w-4 h-4 flex-shrink-0 ${alert.severity === 'red' ? 'animate-pulse' : ''}`} />
          <span className="truncate">{cam.highway} · {alertLabel(alert)}</span>
        </div>
      )}

      {/* top label */}
      <div className={`absolute inset-x-0 bg-gradient-to-b from-black/85 to-transparent px-3 pb-6 pointer-events-none ${alert ? 'top-8 pt-1' : 'top-0 pt-2'}`}>
        <div className="flex items-center gap-2 min-w-0">
          {cam.highway && <span className="text-[10px] font-black bg-teal-500 text-black px-1.5 py-0.5 rounded flex-shrink-0">{cam.highway}</span>}
          <span className="font-semibold text-white text-sm truncate">{cam.name}</span>
        </div>
        <div className="text-[11px] text-zinc-300 truncate flex items-center gap-1.5 mt-0.5">
          <MapPin className="w-3 h-3 flex-shrink-0" />{[cam.region, cam.direction].filter(Boolean).join(' · ')}
        </div>
      </div>

      {/* bottom status */}
      {!offline && (
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-transparent px-3 pb-2 pt-6 flex items-center justify-between pointer-events-none">
          <span className="flex items-center gap-1.5 text-[11px] text-teal-400 font-semibold">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" />
            </span>
            LIVE
          </span>
          <span className="text-[11px] text-zinc-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />{loadedAt ? dayjs(loadedAt).fromNow() : 'updating…'}
          </span>
        </div>
      )}

      <button
        onClick={onRemove}
        data-testid={`cctv-remove-${cam.id}`}
        className="absolute top-2 right-2 p-1.5 rounded-lg bg-black/60 text-white opacity-0 group-hover:opacity-100 transition hover:bg-red-600"
        title="Remove from wall"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function EmptySlot({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="noc-card border-dashed !border-white/15 flex flex-col items-center justify-center gap-2 text-zinc-600 hover:text-teal-400 hover:!border-teal-500/40 transition"
      data-testid="cctv-empty-slot"
    >
      <Camera className="w-7 h-7" />
      <span className="text-xs uppercase tracking-widest">Add camera</span>
    </button>
  );
}

function EmptyState({ onOpen, loading }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center gap-4">
      <div className="w-16 h-16 rounded-2xl bg-teal-500/10 flex items-center justify-center">
        <Camera className="w-8 h-8 text-teal-400" />
      </div>
      <div>
        <div className="font-display font-black text-2xl">CCTV Wall</div>
        <div className="text-zinc-400 mt-1 max-w-md">
          {loading ? 'Loading camera catalogue…' : 'Pick the NZTA traffic cameras you want to watch. They auto-refresh every few seconds.'}
        </div>
      </div>
      {!loading && (
        <button onClick={onOpen} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-teal-500 text-black font-bold hover:bg-teal-400 transition" data-testid="cctv-empty-configure">
          <SlidersHorizontal className="w-4 h-4" /> Choose cameras
        </button>
      )}
    </div>
  );
}

function FeedPill({ feed }) {
  const ok = feed?.online;
  return (
    <div className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${ok ? 'bg-teal-500/10 text-teal-400' : 'bg-red-500/10 text-red-400'}`}>
      {ok ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
      <span className="font-semibold tracking-wider uppercase">NZTA CCTV {ok ? 'Online' : '—'}</span>
    </div>
  );
}

function PresetsMenu({ presets, setPresets, current, layout, onLoad }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const save = () => {
    const n = name.trim();
    if (!n) return;
    setPresets((prev) => [...prev.filter((p) => p.name !== n), { id: String(Date.now()), name: n, cameras: current, layout }]);
    setName('');
  };
  const del = (id) => setPresets((prev) => prev.filter((p) => p.id !== id));

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="cctv-presets"
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition ${
          open ? 'bg-teal-500 text-black' : 'bg-white/5 text-zinc-200 hover:bg-white/10'
        }`}
      >
        <Bookmark className="w-4 h-4" /> Presets
        {presets.length > 0 && <span className="text-xs opacity-70">({presets.length})</span>}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-xl border border-white/10 bg-neutral-900 shadow-2xl z-[60] p-2" data-testid="cctv-presets-menu">
          {presets.length === 0 && (
            <div className="text-xs text-zinc-500 px-2 py-3 text-center">No saved presets yet. Save the current wall below.</div>
          )}
          {presets.map((p) => (
            <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/5" data-testid={`cctv-preset-${p.id}`}>
              <button onClick={() => { onLoad(p); setOpen(false); }} className="flex-1 text-left min-w-0" data-testid={`cctv-preset-load-${p.id}`}>
                <div className="text-sm font-medium text-white truncate">{p.name}</div>
                <div className="text-[11px] text-zinc-500">{(p.cameras || []).length} cameras · {LAYOUTS[p.layout]?.label || p.layout}</div>
              </button>
              <button onClick={() => del(p.id)} className="p-1.5 rounded text-zinc-500 hover:text-red-400" data-testid={`cctv-preset-del-${p.id}`} title="Delete preset">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="border-t border-white/10 mt-2 pt-2 flex items-center gap-2 px-1">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
              placeholder="Save current as…"
              data-testid="cctv-preset-name"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-teal-500"
            />
            <button onClick={save} data-testid="cctv-preset-save" className="p-1.5 rounded-lg bg-teal-500 text-black hover:bg-teal-400" title="Save preset">
              <BookmarkPlus className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function ConfigPanel({ cameras, selected, capacity, onToggle, onClear, onClose }) {
  const [q, setQ] = useState('');
  const [island, setIsland] = useState('all');
  const [region, setRegion] = useState('all');
  const [hideOffline, setHideOffline] = useState(false);
  const listRef = useRef(null);

  const scrollList = (dir) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollBy({ top: dir * Math.max(200, el.clientHeight * 0.8), behavior: 'smooth' });
  };

  const regions = useMemo(() => {
    const set = new Set();
    cameras.forEach((c) => { if (c.region) set.add(c.region); });
    return Array.from(set).sort();
  }, [cameras]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return cameras.filter((c) => {
      if (island !== 'all' && c.island !== island) return false;
      if (region !== 'all' && c.region !== region) return false;
      if (hideOffline && c.offline) return false;
      if (term) {
        const hay = `${c.name} ${c.description} ${c.highway || ''} ${c.region || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [cameras, q, island, region, hideOffline]);

  const overCap = selected.length > capacity;

  return (
    <div className="fixed inset-0 z-[200] flex justify-end" data-testid="cctv-config-panel">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-neutral-950 border-l border-white/10 flex flex-col shadow-2xl">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div>
            <div className="font-display font-black text-lg text-white flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-teal-400" /> Select cameras
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              <span className={overCap ? 'text-orange-400 font-semibold' : ''}>{selected.length} selected</span> · wall shows {capacity}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-zinc-300" data-testid="cctv-config-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {overCap && (
          <div className="mx-5 mt-3 text-xs bg-orange-500/10 text-orange-300 border border-orange-500/30 rounded-lg px-3 py-2">
            You’ve picked more than the current layout shows. Increase the layout, or turn on Auto-rotate (↻ in the top bar) to sweep through them all.
          </div>
        )}

        {/* filters */}
        <div className="px-5 py-3 space-y-2 border-b border-white/10 flex-shrink-0">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, highway, region…"
              data-testid="cctv-search"
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-teal-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={island} onChange={(e) => setIsland(e.target.value)} data-testid="cctv-filter-island"
              style={{ colorScheme: 'dark' }}
              className="bg-neutral-900 border border-white/10 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-teal-500">
              <option value="all" className="bg-neutral-900 text-white">All islands</option>
              <option value="north" className="bg-neutral-900 text-white">North Island</option>
              <option value="south" className="bg-neutral-900 text-white">South Island</option>
            </select>
            <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="cctv-filter-region"
              style={{ colorScheme: 'dark' }}
              className="bg-neutral-900 border border-white/10 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-teal-500">
              <option value="all" className="bg-neutral-900 text-white">All regions</option>
              {regions.map((r) => <option key={r} value={r} className="bg-neutral-900 text-white">{r}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none">
            <input type="checkbox" checked={hideOffline} onChange={(e) => setHideOffline(e.target.checked)} className="accent-teal-500" />
            Hide offline / under-maintenance cameras
          </label>
        </div>

        {/* list */}
        <div className="relative flex-1 min-h-0" style={{ backgroundColor: '#0a0a0a' }}>
          <div ref={listRef} className="absolute inset-0 overflow-y-auto px-3 py-2" style={{ backgroundColor: '#0a0a0a', WebkitOverflowScrolling: 'touch' }}>
          {filtered.length === 0 && (
            <div className="text-center text-zinc-500 text-sm py-10">No cameras match your filters.</div>
          )}
          {filtered.map((c) => {
            const checked = selected.includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() => onToggle(c.id)}
                onFocus={(e) => e.currentTarget.scrollIntoView({ block: 'nearest' })}
                data-testid={`cctv-option-${c.id}`}
                className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition ${
                  checked ? 'bg-teal-500/10' : 'hover:bg-white/5'
                }`}
              >
                <span className={`w-5 h-5 rounded flex items-center justify-center border flex-shrink-0 ${
                  checked ? 'bg-teal-500 border-teal-500 text-black' : 'border-white/25'
                }`}>
                  {checked && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    {c.highway && <span className="text-[10px] font-black bg-white/10 text-teal-300 px-1.5 py-0.5 rounded">{c.highway}</span>}
                    <span className="text-sm font-medium text-white truncate">{c.name}</span>
                    {c.offline && <span className="text-[10px] text-red-400 uppercase tracking-wide flex-shrink-0">offline</span>}
                  </span>
                  <span className="block text-[11px] text-zinc-500 truncate">{[c.region, c.direction].filter(Boolean).join(' · ')}</span>
                </span>
              </button>
            );
          })}
          </div>
          {/* TV-friendly scroll buttons (remotes can't wheel-scroll a nested list) */}
          <div className="absolute right-2 bottom-2 flex flex-col gap-2 z-10">
            <button onClick={() => scrollList(-1)} data-testid="cctv-list-scroll-up"
              className="w-11 h-11 rounded-full bg-teal-500 text-black shadow-lg flex items-center justify-center hover:bg-teal-400 active:scale-95 transition" title="Scroll up">
              <ChevronUp className="w-6 h-6" strokeWidth={3} />
            </button>
            <button onClick={() => scrollList(1)} data-testid="cctv-list-scroll-down"
              className="w-11 h-11 rounded-full bg-teal-500 text-black shadow-lg flex items-center justify-center hover:bg-teal-400 active:scale-95 transition" title="Scroll down">
              <ChevronDown className="w-6 h-6" strokeWidth={3} />
            </button>
          </div>
        </div>

        {/* footer */}
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between flex-shrink-0">
          <button onClick={onClear} className="text-sm text-zinc-400 hover:text-red-400 transition" data-testid="cctv-clear">
            Clear all
          </button>
          <div className="text-xs text-zinc-500 flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3" /> refresh 12s
          </div>
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-teal-500 text-black font-bold text-sm hover:bg-teal-400 transition" data-testid="cctv-config-done">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
